import { useState, useEffect } from "react";
import { Button } from "../../base/Button";
import { getButtonClassName } from "../../base/Button/Button";
import type { ComponentType, ReactNode } from "react";
import { Snackbar } from "../../base/Snackbar";

export interface CodingLessonFooterProps {
  /** 次のレッスンのタイトル */
  nextLessonTitle?: string;
  /** 次のレッスンの番号 */
  nextLessonNumber?: number;
  /** 次のレッスンが無効か（最後のレッスンの場合など） */
  nextLessonDisabled?: boolean;
  /** 次のレッスンの題が無くても、右の主の釦を出す。コースの最後のレッスンで「レッスンを完了する」を押せるようにする */
  showNextAction?: boolean;
  /** 前のレッスンのタイトル */
  prevLessonTitle?: string;
  /** 前のレッスンの番号 */
  prevLessonNumber?: number;
  /** 前のレッスンが無効か */
  prevLessonDisabled?: boolean;
  /** ホームボタンのクリックハンドラ */
  onHomeClick?: () => void;
  /** ホームボタンの中身。渡すと家の印と「ホームに戻る」の代わりに出す (サイトのロゴなど) */
  homeContent?: ReactNode;
  /** homeContent を渡したときの読み上げ名 */
  homeLabel?: string;
  /** 次のレッスンボタンのクリックハンドラ */
  onNextLessonClick?: () => void;
  /** 前のレッスンボタンのクリックハンドラ */
  onPrevLessonClick?: () => void;
  /** カスタムクラス名 */
  className?: string;
  /** 次のレッスンボタンをプライマリ（緑）スタイルにするか */
  primaryButton?: boolean;
  secondaryButtonVariant?: "ghost" | "outline";
  /** Snackbar表示 */
  showSnackbar?: boolean;
  /** Snackbar1のメッセージ（XP獲得など） */
  snackbarMessage?: string;
  /** Snackbar2のアクションテキスト（次のレッスンへ） */
  snackbarActionText?: string;
  /** Snackbar2のアクションクリックハンドラ */
  onSnackbarAction?: () => void;
  /** 次のレッスンボタンのローディング状態 */
  nextLessonLoading?: boolean;
  /**
   * 帯の上端に引く読了バーの割合 (0〜100)。渡さないと引かない。
   *
   * **どこまで来たかを誰も言っていなかった。** 本文が 5 画面ぶんある回でも、
   * 残りが分かるものが画面に 1 つも無い
   */
  progressPercent?: number;
  /**
   * コースの中でこの回が何本目か。渡すと細いバーと数字を出す。
   *
   * **数字だけでは進み具合が読めない。** 「1 / 36」は読めば分かるが、
   * 目を上げた一瞬では位置として入ってこない。長さで見せる
   */
  courseProgress?: { current: number; total: number };
  courseProgressLabel?: string;
  /** 数が本数でなく番号のときの読み上げ (既定は「コース全 N 本のうち M 本目」) */
  courseProgressAriaLabel?: string;
  /**
   * 現在地を「49 本目 (全 212 本)」の文で出す。札の番号 (表示順) と並んだとき、
   * 「49 / 212」が別の番号に見えないようにする。読了の数を出すときは使わない
   */
  courseProgressOrdinal?: boolean;
  /**
   * 読み終えた本数。**現在地とは別のもの。** 1 本目に居るあいだ
   * 現在地は 1 のままだが、読了は 0 から 1 へ動く。動くほうを見せたい
   * ので、1 本でも読了があればそちらに切り替える
   */
  readCount?: number;
  /** 「残り約 5 分」のような見込み。渡さないと出さない */
  remainingLabel?: string;
  /**
   * 次へ進むボタンの文言。既定は「次のレッスンへ」。
   *
   * **単位の名前が画面ごとに違う。** 学習パスの面では 1 つが「レッスン」
   * ではなく「学習パス」なので、同じ帯を使いながら呼び名だけ差し替える。
   * ここを渡さずに使うと、パスの一覧でだけ「レッスン」と呼ぶことになる
   */
  nextActionLabel?: string;
  /**
   * 次へ進むボタンの大きさ。既定は md。
   * 学習パスは 1 画面に 1 つしか進む道が無いので lg にして、
   * そこが出口だと一目で分かるようにしている
   */
  nextActionSize?: "md" | "lg";
  /** 「次は …」の前置き。既定は「次は」 */
  nextLeadLabel?: string;
  /**
   * 前へ戻るボタンの読み上げ名。既定は「前のレッスンへ」。
   *
   * 読み上げでは後ろに前の題名を足す。狭い画面では題名が隠れ、短い字だけになるため
   */
  prevActionLabel?: string;
  /** 狭い画面で矢印の隣に出す短い字。既定は「前へ」 */
  prevShortLabel?: string;
  homeActionLabel?: string;
  homeHref?: string;
  nextHref?: string;
  LinkComponent?: ComponentType<{ to: string; className?: string; children: ReactNode }>;
  /** 左端、ホームの前に置くもの (上の帯を持たない画面の、引き出しを開く口など)。渡さない画面は今までどおり */
  leading?: ReactNode;
  /** ホームと進みの間に置くもの (上の帯を持たない画面のパンくずなど)。渡さない画面は今までどおり */
  trail?: ReactNode;
  /** ホームの釦に足すクラス (狭い画面で隠すなど) */
  homeClassName?: string;
}

/** 0〜100 に収める。外から来る値を信じない */
function clampPercent(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.min(100, Math.max(0, value));
}

// ホームアイコンSVG
function HomeIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="flex-shrink-0"
    >
      <path
        d="M2 6L8 2L14 6V13C14 13.5304 13.7893 14.0391 13.4142 14.4142C13.0391 14.7893 12.5304 15 12 15H4C3.46957 15 2.96086 14.7893 2.58579 14.4142C2.21071 14.0391 2 13.5304 2 13V6Z"
        stroke="#727270"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M6 15V8H10V15"
        stroke="#727270"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// 左矢印アイコンSVG（前のレッスン用）
function AngleLeftIcon({ color = "#05b45b" }: { color?: string }) {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="flex-shrink-0"
    >
      <path
        d="M10 12L6 8L10 4"
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// 右矢印アイコンSVG（次のレッスン用）
function AngleRightIcon({ color = "white" }: { color?: string }) {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="flex-shrink-0"
    >
      <path
        d="M7.5 15L12.5 10L7.5 5"
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// 番号をフォーマット（01, 02, ...）
function formatLessonNumber(num: number): string {
  return num.toString().padStart(2, "0");
}

export function CodingLessonFooter({
  nextLessonTitle,
  nextLessonNumber,
  nextLessonDisabled = false,
  showNextAction = false,
  prevLessonTitle,
  prevLessonNumber,
  prevLessonDisabled = false,
  onHomeClick,
  homeContent,
  homeLabel,
  onNextLessonClick,
  onPrevLessonClick,
  className = "",
  primaryButton = false,
  secondaryButtonVariant = "ghost",
  showSnackbar = false,
  snackbarMessage = "",
  snackbarActionText,
  onSnackbarAction,
  nextLessonLoading = false,
  progressPercent,
  courseProgress,
  courseProgressLabel,
  courseProgressAriaLabel,
  courseProgressOrdinal,
  readCount,
  remainingLabel,
  nextActionLabel = "次のレッスンへ",
  nextActionSize = "md",
  nextLeadLabel = "次は",
  prevActionLabel = "前のレッスンへ",
  prevShortLabel = "前へ",
  homeActionLabel = "ホームに戻る",
  homeHref,
  nextHref,
  LinkComponent,
  leading,
  trail,
  homeClassName = "",
}: CodingLessonFooterProps) {
  /* 読了が 1 本でもあるか。0 のときは現在地を出す (今までどおり) */
  const hasRead = typeof readCount === "number" && readCount > 0;
  const prevTitleFull = prevLessonNumber
    ? `${formatLessonNumber(prevLessonNumber)}. ${prevLessonTitle}`
    : prevLessonTitle;

  // 2つ目のSnackbarを遅延表示
  const [showSecondSnackbar, setShowSecondSnackbar] = useState(false);

  useEffect(() => {
    if (showSnackbar && snackbarActionText) {
      const timer = setTimeout(() => {
        setShowSecondSnackbar(true);
      }, 300); // 300ms後に2つ目を表示
      return () => clearTimeout(timer);
    } else {
      setShowSecondSnackbar(false);
    }
  }, [showSnackbar, snackbarActionText]);

  return (
    // 画面の最下部に貼り付ける。前後のレッスンへの導線は、本文のどこを
    // 読んでいても届いてほしい。長い回だと最後までスクロールしないと
    // 「次へ」が出てこなかった。
    //
    // relative から sticky へ替えても Snackbar の位置は変わらない。
    // sticky も絶対配置の基準になるので、absolute bottom-full はこの箱を
    // 基準に上へ出る
    <div
      className={`sticky bottom-0 z-30 pb-[env(safe-area-inset-bottom)] ${className}`}
    >
      {/* Snackbar - フッターの上、右側に表示 */}
      <div className="absolute bottom-full right-6 z-10 flex flex-col items-end gap-1">
        {/* Snackbar1: メッセージ（XP獲得など） */}
        <Snackbar
          message={snackbarMessage}
          icon="check"
          isVisible={showSnackbar}
        />
        {/* Snackbar2: アクション（次のレッスンへ） */}
        <Snackbar
          message={snackbarActionText || ""}
          icon="right"
          iconPosition="right"
          isVisible={showSecondSnackbar}
          onAction={onSnackbarAction}
          loading={nextLessonLoading}
        />
      </div>

      {/* 本文の上に重なるので、地との境目を線で出す。地と同系色のまま
          重ねると、文章の続きなのか操作列なのかが読めない */}
      <div className="relative flex items-center justify-between gap-2 md:gap-4 h-14 px-2 min-[380px]:px-3 sm:px-4 md:px-6 bg-main border-t border-main">
        {/*
        読了バーは帯の上端に重ねる。**進み具合と次への導線を同じ場所に置く。**
        離すと、残りを見るときと進むときで目を動かす先が変わる
        */}
        {typeof progressPercent === "number" && (
          <div
            className="pointer-events-none absolute inset-x-0 top-0 h-[3px] bg-third"
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round(clampPercent(progressPercent))}
            aria-label="このレッスンを読んだ割合"
          >
            <div
              className="h-full bg-primary transition-[width] duration-200 ease-out motion-reduce:transition-none"
              style={{ width: `${clampPercent(progressPercent)}%` }}
            />
          </div>
        )}

        <div className="flex min-w-0 items-center gap-2 md:shrink-0 md:gap-4">
          {leading}
          {/* ホームに戻るボタン */}
          {/* 帯に出しっぱなしの導線なので、sm の 36px ではなく 44px を取る */}
          {homeHref && LinkComponent ? <LinkComponent
            to={homeHref}
            className={getButtonClassName({
              variant: "ghost",
              size: "sm",
              children: true,
              className: "min-h-11 min-w-11 gap-2",
            })}
          >
            <HomeIcon />
            <span className="hidden md:inline whitespace-nowrap">{homeActionLabel}</span>
            <span className="sr-only md:hidden">{homeActionLabel}</span>
          </LinkComponent> : <Button
            variant="ghost"
            size="sm"
            onClick={onHomeClick}
            className={`min-h-11 min-w-11 gap-2 ${homeClassName}`}
            aria-label={homeContent ? homeLabel : undefined}
          >
            {homeContent ?? (
              <>
                <HomeIcon />
                <span className="hidden md:inline whitespace-nowrap">
                  {homeActionLabel}
                </span>
              </>
            )}
          </Button>}

          {/* パンくずは lg 以上でだけ出る前提。幅に上限を置き、左の群れが
              右の操作列の場所まで取らないようにする */}
          {trail && (
            <div className="hidden min-w-0 max-w-[10rem] overflow-hidden lg:flex xl:max-w-[14rem]">
              {trail}
            </div>
          )}

          {/* 現在地と残り。狭い画面では件数だけ残す */}
          {(courseProgress || remainingLabel) && (
            <p
              data-footer-position
              className="flex min-w-0 items-center gap-2 overflow-hidden max-[360px]:hidden max-md:h-6 max-md:flex-wrap max-md:content-start whitespace-nowrap md:shrink-0 md:overflow-visible font-noto max-sm:text-[0.9375rem] text-[13px] text-sub"
            >
              {/*
              スマホでは、数が入りきらないとき**数ごと**次の行へ送って隠す。
              これが無いと、右の釦に押された数が「179 本」と途中で切れて読めない。
              幅 0 の目印を先頭に置き、数がその隣に収まらなければ折り返される。
              行の高さを 1 行分に絞って、折り返された数は見えなくする
              */}
              <span aria-hidden="true" className="-mr-2 h-5 w-0 md:hidden" />
              {courseProgress && courseProgress.total > 0 && (
                <>
                  {/*
                  読了が 1 本でもあれば、現在地ではなく読了の数を出す。
                  **「1 / 36」は 1 本読み終えても動かない。** 現在地なので
                  当然なのだが、読んだ人からは「何も起きなかった」に見える。
                  読了は 0 から 1 へ動くので、そちらに切り替える。
                  どちらを見ているかは「読了」の 2 文字で分ける。

                  **1 本目でも線が見えるように下限を引く。** 1/36 は 2.8% で、
                  素直に描くと 1px 未満になって「何も無い」ように見える
                  */}
                  <span
                    role="progressbar"
                    aria-valuemin={0}
                    aria-valuemax={courseProgress.total}
                    aria-valuenow={
                      hasRead ? readCount! : courseProgress.current
                    }
                    aria-label={
                      !hasRead && courseProgressAriaLabel
                        ? courseProgressAriaLabel
                        : hasRead
                        ? `コース全 ${courseProgress.total} 本のうち ${readCount} 本を読了`
                        : `コース全 ${courseProgress.total} 本のうち ${courseProgress.current} 本目`
                    }
                    className="hidden h-1.5 w-16 shrink-0 overflow-hidden rounded-full bg-third sm:block md:hidden lg:block"
                  >
                    <span
                      className="block h-full rounded-full bg-primary transition-[width] duration-500 ease-out motion-reduce:transition-none"
                      style={{
                        width: `${Math.max(
                          6,
                          Math.min(
                            100,
                            ((hasRead ? readCount! : courseProgress.current) /
                              courseProgress.total) *
                              100,
                          ),
                        )}%`,
                      }}
                    />
                  </span>
                  {courseProgressLabel && <span className="truncate md:max-w-[16rem]" title={courseProgressLabel}>{courseProgressLabel}</span>}
                  <span data-footer-count className="shrink-0">
                    {hasRead && (
                      <span className="mr-1 font-bold text-primary max-[390px]:hidden">読了</span>
                    )}
                    {!hasRead && courseProgressOrdinal ? (
                      <>
                        {/* 全体の本数は消す順の先頭。前の題名が 120px を割る前に、2xl 未満では引っ込める */}
                        {`${courseProgress.current} 本目`}
                        <span className="hidden 2xl:inline">{` (全 ${courseProgress.total} 本)`}</span>
                      </>
                    ) : (
                      <>
                        {hasRead ? readCount : courseProgress.current} /{" "}
                        {courseProgress.total}
                      </>
                    )}
                  </span>
                </>
              )}
              {/* 中黒は残りと一緒に消す。狭い画面で「1 / 36 ・」と
                  後ろに何も無い中黒だけが残っていた */}
              {courseProgress && remainingLabel && (
                <span aria-hidden="true" className="hidden shrink-0 text-third md:inline">
                  ・
                </span>
              )}
              {/* スマホでは残りを畳み、件数だけにする。390px で「13 / 30 残り約 …」と
                  途中で切れ、切れた残りは何分か読めないまま件数の横幅だけを食っていた */}
              {remainingLabel && <span className="hidden min-w-0 truncate md:inline">{remainingLabel}</span>}
            </p>
          )}
        </div>

        {/* 右側のナビゲーションボタン */}
        <div className="flex shrink-0 items-center gap-2 md:min-w-0 md:shrink md:gap-4">
          {/* 前のレッスンボタン */}
          {prevLessonTitle && (
            <div className="shrink-0 md:min-w-0 md:shrink">
              <Button
                variant="ghost"
                size="md"
                onClick={onPrevLessonClick}
                disabled={prevLessonDisabled}
                aria-label={`${prevActionLabel} ${prevTitleFull}`}
                className="max-w-full gap-2 max-sm:min-w-11 max-sm:px-3 md:min-w-0"
              >
                <AngleLeftIcon
                  color={prevLessonDisabled ? "#727270" : "#05b45b"}
                />
                {/* スマホでは題名の代わりに短い字だけ。題名まで出すと「次へ」の場所が無くなり、矢印だけでは押す先が読めない */}
                <span aria-hidden="true" data-footer-prev-short className="whitespace-nowrap font-noto max-sm:text-[0.9375rem] text-[13px] md:hidden">
                  {prevShortLabel}
                </span>
                <span
                  data-footer-prev-title
                  title={prevTitleFull}
                  className="hidden min-w-0 md:block md:max-w-[16rem] lg:max-w-[22rem] truncate"
                >
                  {prevTitleFull}
                </span>
              </Button>
            </div>
          )}

          {/*
          ここがページでいちばん押してほしいボタン。

          **ボタンには動詞を書く。** 以前は「02. なぜWebサービスは攻撃
          されるのか ＞」とレッスンの題名だけが乗っていた。題名は
          「これが次だ」とは言っていても「押せ」とは言っていない。
          初めて来た人はこれを操作だと読まない。題名はボタンの左に
          小さく添えて、押す先が何かは残す
          */}
          {(nextLessonTitle || showNextAction) && (
            <>
              {nextLessonTitle && <p
                data-footer-next-title
                title={`${nextLeadLabel} ${nextLessonNumber ? `${formatLessonNumber(nextLessonNumber)}. ${nextLessonTitle}` : nextLessonTitle}`}
                className="hidden min-w-0 max-w-[18rem] truncate font-noto max-sm:text-[0.9375rem] text-[13px] text-sub xl:block"
              >
                {nextLeadLabel}{" "}
                {nextLessonNumber
                  ? `${formatLessonNumber(nextLessonNumber)}. ${nextLessonTitle}`
                  : nextLessonTitle}
              </p>}
              {/* pressable で押し込みの縁が付く。**ページでいちばん
                  押してほしいボタンなので、押した手応えを返す** */}
              {nextHref && LinkComponent ? <LinkComponent
                to={nextHref}
                className={getButtonClassName({
                  variant: primaryButton ? "primary" : secondaryButtonVariant,
                  size: nextActionSize,
                  children: true,
                  pressable: primaryButton || secondaryButtonVariant !== "outline",
                  className: "gap-2 max-sm:px-3",
                })}
              >
                <span className="whitespace-nowrap">{nextActionLabel}</span>
                <AngleRightIcon color={primaryButton ? "white" : "#727270"} />
              </LinkComponent> : <Button
                variant={primaryButton ? "primary" : secondaryButtonVariant}
                size={nextActionSize}
                pressable={primaryButton || secondaryButtonVariant !== "outline"}
                onClick={onNextLessonClick}
                disabled={nextLessonDisabled}
                aria-label={nextActionLabel}
                className="gap-2 max-sm:px-3"
              >
                <span className="whitespace-nowrap">{nextActionLabel}</span>
                <AngleRightIcon
                  color={
                    nextLessonDisabled
                      ? "#727270"
                      : primaryButton
                        ? "white"
                        : "#727270"
                  }
                />
              </Button>}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
